import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Car, Bell, LogOut, Clock, CheckCircle, 
  Loader2, User, Play, Check, MapPin, Zap,
  Plus, Phone, Search, ParkingCircle, UserPlus, PhoneCall, Timer,
  FileText, CheckCircle2, XCircle, AlertTriangle, Send
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API, BACKEND_URL } from "@/App";
import SignaturePad from "@/components/SignaturePad";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const ValetDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const pushNotif = usePushNotifications({ role: "valet_staff", venueId: user?.venue_id, userId: user?.user_id });
  const [requests, setRequests] = useState([]);
  const [walkinGuests, setWalkinGuests] = useState([]);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [estimatedWait, setEstimatedWait] = useState(null);
  const wsRef = useRef(null);

  // Walk-in registration form
  const [walkinDialogOpen, setWalkinDialogOpen] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [carDetails, setCarDetails] = useState("");
  const [selectedSpot, setSelectedSpot] = useState("");
  const [guestNotes, setGuestNotes] = useState("");
  const [tableNumber, setTableNumber] = useState("");

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Waiver dialog
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [waiverGuest, setWaiverGuest] = useState(null);
  const [waiverText, setWaiverText] = useState("");
  const [waiverLoading, setWaiverLoading] = useState(false);

  // Fetch waiver text on mount
  useEffect(() => {
    const fetchWaiverText = async () => {
      try {
        const response = await axios.get(`${API}/waiver/text`, { withCredentials: true });
        setWaiverText(response.data.waiver_text);
      } catch (error) {
        console.error("Error fetching waiver text:", error);
      }
    };
    fetchWaiverText();
  }, []);

  // Fetch estimated wait time
  const fetchEstimatedWait = useCallback(async () => {
    if (!user?.venue_id) return;
    try {
      const response = await axios.get(`${API}/estimated-wait/${user.venue_id}`, { withCredentials: true });
      setEstimatedWait(response.data);
    } catch (error) {
      console.error("Error fetching estimated wait:", error);
    }
  }, [user?.venue_id]);

  const fetchData = useCallback(async () => {
    try {
      const requests_to_make = [
        axios.get(`${API}/valet/venue-requests`, { withCredentials: true }),
        axios.get(`${API}/walkin/guests`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      ];
      
      // Only fetch parking spots if user has venue_id
      if (user?.venue_id) {
        requests_to_make.push(axios.get(`${API}/parking/spots/${user.venue_id}`, { withCredentials: true }));
      }

      const results = await Promise.all(requests_to_make);
      setRequests(results[0].data);
      setWalkinGuests(results[1].data);
      setNotifications(results[2].data);
      setUnreadCount(results[3].data.count);
      if (results[4]) {
        setParkingSpots(results[4].data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.venue_id]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user?.venue_id) {
      fetchData();
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }

    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/venue/${user.venue_id}`;

    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          console.log('WebSocket connected to venue');
          fetchData();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_request') {
              setRequests(prev => [data.request, ...prev]);
              toast.success(`New request from ${data.request.user_name}!`, { icon: '🚗' });
              setUnreadCount(prev => prev + 1);
            } else if (data.type === 'walkin_request') {
              setWalkinGuests(prev => prev.map(g => 
                g.guest_id === data.guest.guest_id ? data.guest : g
              ));
              toast.success(`Walk-in guest ${data.guest.guest_name} requested their car!`, { icon: '🚗' });
            } else if (data.type === 'status_update') {
              setRequests(prev => prev.map(req => 
                req.request_id === data.request_id ? { ...req, status: data.status } : req
              ));
            }
          } catch (e) {
            console.error('WebSocket message error:', e);
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          setTimeout(connectWebSocket, 3000);
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };

    connectWebSocket();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [user?.venue_id, fetchData]);

  useEffect(() => {
    fetchData();
    fetchEstimatedWait();
    // Refresh estimated wait every 30 seconds
    const waitInterval = setInterval(fetchEstimatedWait, 30000);
    return () => clearInterval(waitInterval);
  }, [fetchData, fetchEstimatedWait]);

  const handleRegisterWalkin = async () => {
    if (!guestName || !guestPhone || !carDetails) {
      toast.error("Please fill in guest name, phone, and car details");
      return;
    }

    try {
      await axios.post(`${API}/walkin/register`, {
        venue_id: user?.venue_id,
        guest_name: guestName,
        guest_phone: guestPhone,
        car_details: carDetails,
        parking_spot: selectedSpot || null,
        table_number: tableNumber || null,
        notes: guestNotes || null
      }, { withCredentials: true });

      toast.success("Walk-in guest registered successfully!");
      setWalkinDialogOpen(false);
      setGuestName("");
      setGuestPhone("");
      setCarDetails("");
      setSelectedSpot("");
      setTableNumber("");
      setGuestNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register guest");
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdatingStatus(requestId);
    try {
      await axios.put(`${API}/valet/request/${requestId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const updateWalkinStatus = async (guestId, newStatus) => {
    setUpdatingStatus(guestId);
    try {
      await axios.put(`${API}/walkin/${guestId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const requestWalkinCar = async (guestId) => {
    try {
      const response = await axios.put(`${API}/walkin/${guestId}/request-car`, {}, { withCredentials: true });
      toast.success(`Car requested! Spot: ${response.data.parking_spot || 'N/A'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request car");
    }
  };

  const callGuestViaApp = async (guestId, guestName) => {
    try {
      await axios.post(`${API}/walkin/${guestId}/call`, {}, { 
        withCredentials: true,
        params: { message: "Your car is ready. Please proceed to the exit." }
      });
      toast.success(`Notification sent to ${guestName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    }
  };

  const sendTrackingLink = async (guestId, guestName) => {
    try {
      const res = await axios.post(`${API}/walkin/${guestId}/send-tracking-link`, {}, { withCredentials: true });
      if (res.data.sms_mock) {
        toast.success(`Tracking link generated for ${guestName} (SMS not configured — link: ${res.data.tracking_url})`);
      } else {
        toast.success(`Tracking link sent to ${guestName}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send tracking link");
    }
  };

  // Waiver functions
  const openWaiverDialog = (guest) => {
    setWaiverGuest(guest);
    setWaiverDialogOpen(true);
  };

  const [signatureData, setSignatureData] = useState(null);

  const handleAcceptWaiver = async () => {
    if (!waiverGuest) return;
    setWaiverLoading(true);
    try {
      const body = {
        signature_type: signatureData ? "digital" : "verbal",
      };
      if (signatureData) body.signature_image = signatureData;
      await axios.put(`${API}/waiver/${waiverGuest.guest_id}/accept`, body, { withCredentials: true });
      toast.success(`Waiver accepted by ${waiverGuest.guest_name}`);
      setWaiverDialogOpen(false);
      setWaiverGuest(null);
      setSignatureData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept waiver");
    } finally {
      setWaiverLoading(false);
    }
  };

  const handleDeclineWaiver = async () => {
    if (!waiverGuest) return;
    setWaiverLoading(true);
    try {
      await axios.put(`${API}/waiver/${waiverGuest.guest_id}/decline`, {}, { withCredentials: true });
      toast.warning(`Waiver declined by ${waiverGuest.guest_name}. Service may be refused.`);
      setWaiverDialogOpen(false);
      setWaiverGuest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline waiver");
    } finally {
      setWaiverLoading(false);
    }
  };

  const getWaiverBadge = (status) => {
    switch (status) {
      case "accepted":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Waiver Signed</Badge>;
      case "declined":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 border text-xs"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
      case "pending":
      default:
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Waiver Pending</Badge>;
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusConfig = (status) => {
    const config = {
      parked: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Parked", icon: ParkingCircle },
      requested: { color: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Requested", icon: Clock },
      acknowledged: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Acknowledged", icon: CheckCircle },
      fetching: { color: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30", label: "Fetching", icon: Loader2 },
      ready: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Ready", icon: CheckCircle },
      completed: { color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", label: "Completed", icon: Check }
    };
    return config[status] || config.requested;
  };

  const getNextStatus = (currentStatus, isWalkin = false) => {
    if (isWalkin) {
      const flow = { parked: "requested", requested: "fetching", fetching: "ready", ready: "completed" };
      return flow[currentStatus];
    }
    const flow = { requested: "acknowledged", acknowledged: "fetching", fetching: "ready", ready: "completed" };
    return flow[currentStatus];
  };

  const getActionLabel = (status, isWalkin = false) => {
    if (isWalkin) {
      const labels = { parked: "Request Car", requested: "Start Fetching", fetching: "Mark Ready", ready: "Complete" };
      return labels[status] || null;
    }
    const labels = { requested: "Acknowledge", acknowledged: "Start Fetching", fetching: "Mark Ready", ready: "Complete" };
    return labels[status] || null;
  };

  // Filter and combine all active items
  const activeRequests = requests.filter(r => !["completed"].includes(r.status));
  const activeWalkins = walkinGuests.filter(g => !["completed"].includes(g.status));
  const availableSpots = parkingSpots.filter(s => !s.is_occupied);

  // Search filter - valet can only search by token, name, car, or table (not phone for privacy)
  const filteredWalkins = searchQuery 
    ? walkinGuests.filter(g => 
        g.parking_token.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.car_details.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (g.table_number && g.table_number.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : walkinGuests;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090A0F]" data-testid="valet-dashboard">
      {/* Header */}
      <header className="crystal-header sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/logo.svg" alt="VeloPark" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
            <div>
              <span className="font-display text-lg sm:text-xl font-semibold text-white block">VeloPark</span>
              <span className="text-xs text-[#D4AF37]">Valet Staff</span>
            </div>
            {wsConnected && (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full ml-1 sm:ml-2">
                <Zap className="w-3 h-3" />
                <span className="hidden sm:inline">Live</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Push Notification Toggle */}
            {pushNotif.supported && (
              <button
                onClick={pushNotif.isSubscribed ? pushNotif.unsubscribe : pushNotif.subscribe}
                className={`p-2 rounded-lg transition-colors ${pushNotif.isSubscribed ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-gray-400 hover:text-[#D4AF37]"}`}
                title={pushNotif.isSubscribed ? "Notifications enabled" : "Enable push notifications"}
                data-testid="valet-push-toggle"
              >
                <Bell className={`w-4 h-4 ${pushNotif.isSubscribed ? "fill-emerald-400" : ""}`} />
              </button>
            )}
            {/* Register Walk-in Button */}
            <Dialog open={walkinDialogOpen} onOpenChange={setWalkinDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-xs sm:text-sm px-2 sm:px-4" data-testid="register-walkin-btn">
                  <UserPlus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Register Guest</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Register Walk-in Guest</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-white">Guest Name *</Label>
                    <Input
                      placeholder="John Doe"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Phone Number *</Label>
                    <Input
                      placeholder="+1 234 567 8900"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="guest-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Car Details *</Label>
                    <Input
                      placeholder="Red Toyota Camry - ABC 1234"
                      value={carDetails}
                      onChange={(e) => setCarDetails(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="car-details-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Parking Spot</Label>
                    <Select value={selectedSpot} onValueChange={setSelectedSpot}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="parking-spot-select">
                        <SelectValue placeholder="Select spot" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1B21] border-white/10">
                        {availableSpots.map((spot) => (
                          <SelectItem key={spot.spot_id} value={spot.spot_name} className="text-white hover:bg-white/10">
                            {spot.spot_name} {spot.zone && `(${spot.zone})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Table Number</Label>
                    <Input
                      placeholder="T12, VIP-3, Patio-5..."
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="table-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Notes (Optional)</Label>
                    <Input
                      placeholder="VIP guest, special instructions..."
                      value={guestNotes}
                      onChange={(e) => setGuestNotes(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="guest-notes-input"
                    />
                  </div>
                  <Button 
                    className="w-full btn-primary py-6 rounded-xl"
                    onClick={handleRegisterWalkin}
                    data-testid="submit-walkin-btn"
                  >
                    Register & Print Token
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:text-[#D4AF37] hover:bg-white/5 relative"
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 glass-card rounded-xl overflow-hidden z-50">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="text-[#D4AF37] hover:text-[#F0C950] text-xs" onClick={markAllRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-[#A1A1AA]">No notifications</div>
                    ) : (
                      notifications.map((notif) => (
                        <div key={notif.notification_id} className={`p-4 border-b border-white/5 ${!notif.is_read ? 'bg-[#D4AF37]/5' : ''}`}>
                          <p className="text-sm font-medium text-white">{notif.title}</p>
                          <p className="text-xs text-[#A1A1AA] mt-1">{notif.message}</p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-white text-sm hidden sm:block">{user?.name}</span>
            </div>

            <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-white hover:bg-white/5" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <div className="control-panel rounded-xl p-3 sm:p-4" data-testid="stat-app-requests">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider">App Requests</p>
            <p className="text-xl sm:text-3xl font-semibold text-white mt-1">{activeRequests.length}</p>
          </div>
          <div className="control-panel rounded-xl p-3 sm:p-4" data-testid="stat-walkin-guests">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider">Walk-in</p>
            <p className="text-xl sm:text-3xl font-semibold text-white mt-1">{activeWalkins.length}</p>
          </div>
          <div className="control-panel rounded-xl p-3 sm:p-4" data-testid="stat-fetching">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider">Fetching</p>
            <p className="text-xl sm:text-3xl font-semibold text-[#D4AF37] mt-1">
              {requests.filter(r => r.status === "fetching").length + walkinGuests.filter(g => g.status === "fetching").length}
            </p>
          </div>
          <div className="control-panel rounded-xl p-3 sm:p-4" data-testid="stat-ready">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider">Ready</p>
            <p className="text-xl sm:text-3xl font-semibold text-emerald-400 mt-1">
              {requests.filter(r => r.status === "ready").length + walkinGuests.filter(g => g.status === "ready").length}
            </p>
          </div>
          <div className="control-panel rounded-xl p-3 sm:p-4" data-testid="stat-available-spots">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider">Spots</p>
            <p className="text-xl sm:text-3xl font-semibold text-white mt-1">{availableSpots.length}/{parkingSpots.length}</p>
          </div>
          {/* Estimated Wait Time */}
          <div className={`control-panel rounded-xl p-3 sm:p-4 ${estimatedWait?.is_busy ? 'ring-2 ring-amber-500/50' : ''}`} data-testid="stat-est-wait">
            <p className="text-[10px] sm:text-xs text-[#A1A1AA] uppercase tracking-wider flex items-center gap-1">
              <Timer className="w-3 h-3" />
              Wait
            </p>
            <p className="text-xl sm:text-3xl font-semibold text-white mt-1">
              ~{estimatedWait?.estimated_minutes || 3}<span className="text-sm sm:text-lg text-[#A1A1AA]">m</span>
            </p>
            {estimatedWait?.is_busy && (
              <p className="text-[10px] sm:text-xs text-amber-400 mt-1">Busy</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl mb-4 sm:mb-6 w-full flex">
            <TabsTrigger value="active" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1">
              Active ({activeRequests.length + activeWalkins.length})
            </TabsTrigger>
            <TabsTrigger value="walkin" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1">
              Walk-in
            </TabsTrigger>
            <TabsTrigger value="parking" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1">
              Parking
            </TabsTrigger>
          </TabsList>

          {/* Active Requests Tab */}
          <TabsContent value="active">
            {activeRequests.length === 0 && activeWalkins.length === 0 ? (
              <div className="control-panel rounded-xl p-12 text-center">
                <Car className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No active requests</h3>
                <p className="text-[#A1A1AA]">New requests will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* App Requests */}
                {activeRequests.map((request) => {
                  const statusConfig = getStatusConfig(request.status);
                  const StatusIcon = statusConfig.icon;
                  const nextStatus = getNextStatus(request.status);
                  const actionLabel = getActionLabel(request.status);

                  return (
                    <div key={request.request_id} className={`control-panel rounded-xl p-6 ${request.status === "fetching" ? "ring-2 ring-[#D4AF37]/50" : ""}`}>
                      <div className="flex items-center justify-between mb-4">
                        <Badge className={`${statusConfig.color} border px-3 py-1`}>
                          <StatusIcon className={`w-3 h-3 mr-1.5 ${request.status === "fetching" ? "animate-spin" : ""}`} />
                          {statusConfig.label}
                        </Badge>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 border text-xs">APP</Badge>
                      </div>
                      <div className="mb-4">
                        <h3 className="font-medium text-white">{request.user_name}</h3>
                        <p className="text-sm text-[#A1A1AA] flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {request.venue_name}
                        </p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 mb-4">
                        <p className="text-xs text-[#A1A1AA] mb-1">Car Details</p>
                        <p className="text-white font-medium">{request.car_details}</p>
                        {request.parking_spot && (
                          <p className="text-sm text-[#D4AF37] mt-1">Spot: {request.parking_spot}</p>
                        )}
                      </div>
                      {actionLabel && (
                        <Button className="w-full btn-primary" onClick={() => updateStatus(request.request_id, nextStatus)} disabled={updatingStatus === request.request_id}>
                          {updatingStatus === request.request_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                          {actionLabel}
                        </Button>
                      )}
                    </div>
                  );
                })}

                {/* Walk-in Guests */}
                {activeWalkins.map((guest) => {
                  const statusConfig = getStatusConfig(guest.status);
                  const StatusIcon = statusConfig.icon;
                  const nextStatus = getNextStatus(guest.status, true);
                  const actionLabel = getActionLabel(guest.status, true);

                  return (
                    <div key={guest.guest_id} className={`control-panel rounded-xl p-6 ${guest.status === "fetching" ? "ring-2 ring-[#D4AF37]/50" : ""}`}>
                      <div className="flex items-center justify-between mb-4">
                        <Badge className={`${statusConfig.color} border px-3 py-1`}>
                          <StatusIcon className={`w-3 h-3 mr-1.5 ${guest.status === "fetching" ? "animate-spin" : ""}`} />
                          {statusConfig.label}
                        </Badge>
                        <div className="flex items-center gap-2">
                          {getWaiverBadge(guest.waiver_status)}
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-xs">WALK-IN</Badge>
                        </div>
                      </div>
                      <div className="mb-4">
                        <h3 className="font-medium text-white">{guest.guest_name}</h3>
                        {/* Privacy: Show masked phone or call button, not actual number */}
                        <div className="flex items-center gap-2 mt-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-7 px-2"
                            onClick={() => callGuestViaApp(guest.guest_id, guest.guest_name)}
                            data-testid={`call-guest-${guest.guest_id}`}
                          >
                            <PhoneCall className="w-3 h-3 mr-1" />
                            Call via App
                          </Button>
                          {guest.phone_masked && (
                            <span className="text-xs text-[#A1A1AA]">{guest.phone_masked}</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 mb-4">
                        <p className="text-xs text-[#A1A1AA] mb-1">Car Details</p>
                        <p className="text-white font-medium">{guest.car_details}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {guest.table_number && (
                            <span className="text-sm text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded" data-testid={`table-${guest.guest_id}`}>Table: {guest.table_number}</span>
                          )}
                          {guest.parking_spot && (
                            <span className="text-sm text-[#D4AF37]">Spot: {guest.parking_spot}</span>
                          )}
                          <span className="text-sm text-emerald-400">Token: {guest.parking_token}</span>
                        </div>
                      </div>
                      
                      {/* Waiver Button - Show if pending */}
                      {guest.waiver_status === "pending" && (
                        <Button 
                          variant="outline"
                          className="w-full mb-3 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                          onClick={() => openWaiverDialog(guest)}
                          data-testid={`waiver-btn-${guest.guest_id}`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Get Waiver Signature
                        </Button>
                      )}

                      {/* Send Tracking Link - Show for parked guests */}
                      {guest.status === "parked" && (
                        <Button 
                          variant="outline"
                          className="w-full mb-3 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          onClick={() => sendTrackingLink(guest.guest_id, guest.guest_name)}
                          data-testid={`send-link-${guest.guest_id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send Tracking Link
                        </Button>
                      )}
                      
                      {actionLabel && (
                        <Button 
                          className="w-full btn-primary" 
                          onClick={() => guest.status === "parked" ? requestWalkinCar(guest.guest_id) : updateWalkinStatus(guest.guest_id, nextStatus)} 
                          disabled={updatingStatus === guest.guest_id}
                        >
                          {updatingStatus === guest.guest_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                          {actionLabel}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Walk-in Guests Tab */}
          <TabsContent value="walkin">
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                <Input
                  placeholder="Search by token or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 bg-white/5 border-white/10 text-white"
                  data-testid="search-walkin-input"
                />
              </div>
            </div>
            
            {filteredWalkins.length === 0 ? (
              <div className="control-panel rounded-xl p-12 text-center">
                <UserPlus className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No walk-in guests</h3>
                <p className="text-[#A1A1AA]">Register guests using the button above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredWalkins.map((guest) => {
                  const statusConfig = getStatusConfig(guest.status);
                  return (
                    <div key={guest.guest_id} className="control-panel rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                          <Car className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm sm:text-base truncate">{guest.guest_name}</p>
                          <p className="text-xs sm:text-sm text-[#A1A1AA] truncate">{guest.car_details}</p>
                          <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-6 px-2 text-xs"
                              onClick={() => callGuestViaApp(guest.guest_id, guest.guest_name)}
                            >
                              <PhoneCall className="w-3 h-3 mr-1" />
                              Call
                            </Button>
                            {guest.phone_masked && (
                              <span className="text-xs text-[#A1A1AA]">{guest.phone_masked}</span>
                            )}
                            <span className="text-xs text-emerald-400">Token: {guest.parking_token}</span>
                            {guest.parking_spot && <span className="text-xs text-[#D4AF37]">Spot: {guest.parking_spot}</span>}
                            {guest.table_number && <span className="text-xs text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">Table: {guest.table_number}</span>}
                          </div>
                        </div>
                      </div>
                      <Badge className={`${statusConfig.color} border self-start sm:self-center shrink-0`}>{statusConfig.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Parking Map Tab */}
          <TabsContent value="parking">
            {parkingSpots.length === 0 ? (
              <div className="control-panel rounded-xl p-12 text-center">
                <ParkingCircle className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No parking spots configured</h3>
                <p className="text-[#A1A1AA]">Ask admin to set up parking spots for this venue</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
                {parkingSpots.map((spot) => (
                  <div
                    key={spot.spot_id}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center text-center p-2 border ${
                      spot.is_occupied 
                        ? 'bg-red-500/20 border-red-500/30' 
                        : 'bg-emerald-500/20 border-emerald-500/30'
                    }`}
                    title={spot.is_occupied ? spot.current_car : 'Available'}
                  >
                    <span className={`text-sm font-bold ${spot.is_occupied ? 'text-red-400' : 'text-emerald-400'}`}>
                      {spot.spot_name}
                    </span>
                    {spot.zone && <span className="text-[10px] text-[#A1A1AA]">{spot.zone}</span>}
                    {spot.is_occupied && <Car className="w-4 h-4 text-red-400 mt-1" />}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Waiver Dialog */}
        <Dialog open={waiverDialogOpen} onOpenChange={(open) => { setWaiverDialogOpen(open); if (!open) { setSignatureData(null); setWaiverGuest(null); } }}>
          <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Liability Waiver
              </DialogTitle>
              <p className="text-sm text-[#A1A1AA]">Review and confirm the liability waiver with the guest.</p>
            </DialogHeader>
            {waiverGuest && (
              <div className="space-y-4 mt-2">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-[#A1A1AA]">Guest</p>
                  <p className="text-white font-medium">{waiverGuest.guest_name}</p>
                  <p className="text-sm text-[#A1A1AA] mt-1">Car: {waiverGuest.car_details}</p>
                </div>
                <ScrollArea className="h-48 bg-white/5 rounded-lg p-4">
                  <pre className="text-sm text-[#A1A1AA] whitespace-pre-wrap font-sans">{waiverText}</pre>
                </ScrollArea>
                
                {/* Digital Signature Pad */}
                <div className="space-y-2">
                  <p className="text-sm text-white font-medium">Guest Signature {!signatureData && <span className="text-gray-500 font-normal">(optional — or accept verbally)</span>}</p>
                  <SignaturePad 
                    onSave={(data) => setSignatureData(data)} 
                    onClear={() => setSignatureData(null)}
                    width={420}
                    height={140}
                  />
                  {signatureData && (
                    <p className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Signature captured
                    </p>
                  )}
                </div>

                <DialogFooter className="flex gap-3 sm:gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={handleDeclineWaiver}
                    disabled={waiverLoading}
                    data-testid="decline-waiver-btn"
                  >
                    {waiverLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Decline
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleAcceptWaiver}
                    disabled={waiverLoading}
                    data-testid="accept-waiver-btn"
                  >
                    {waiverLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Accept & Sign
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ValetDashboard;
